import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, User, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ImageCropDialog } from "@/components/ui/image-crop-dialog";

interface ProfilePictureUploadProps {
  currentPicture?: string | null;
  firstName?: string;
  lastName?: string;
  onPictureChange: (pictureData: string | null) => Promise<void> | void;
  uploadEndpoint?: string; // API endpoint for uploading to S3
  deleteEndpoint?: string; // API endpoint for deleting from S3
  fetchUrlEndpoint?: string; // API endpoint for fetching presigned URL
  fieldName?: string; // Form field name for upload (default: 'picture')
  size?: "sm" | "md" | "lg" | "xl";
  fallbackIcon?: React.ReactNode;
  showEditOnHover?: boolean;
  // "avatar" = circle, cropped to fill (people). "logo" = rounded square, whole
  // image shown on white, no crop step (company logos don't fit in circles).
  variant?: "avatar" | "logo";
}

export function ProfilePictureUpload({
  currentPicture,
  firstName,
  lastName,
  onPictureChange,
  uploadEndpoint,
  deleteEndpoint,
  fetchUrlEndpoint,
  fieldName = "picture",
  size = "md",
  fallbackIcon,
  showEditOnHover = true,
  variant = "avatar"
}: ProfilePictureUploadProps) {
  const isLogo = variant === "logo";
  const { toast } = useToast();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  // S3-keyed pictures need a presigned URL. Cache it (react-query) so
  // navigating back to the same entity doesn't refetch the URL and the
  // browser can reuse its HTTP cache for the image bytes — presigned URLs
  // are valid for 1h server-side, so a fresh fetch per visit just caused a
  // 1-2s avatar pop-in for no benefit.
  const isS3Picture = !!currentPicture?.startsWith("v3/") && !!fetchUrlEndpoint;
  // Tracks the <img> element's own load lifecycle (Radix reports it) so the
  // fallback can show a skeleton instead of flashing initials while bytes load.
  const [imageStatus, setImageStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const { data: presignedUrl, isLoading: presignedLoading } = useQuery<string | null>({
    queryKey: ["profile-picture-url", fetchUrlEndpoint],
    enabled: isS3Picture,
    staleTime: 30 * 60 * 1000, // half the presign TTL
    queryFn: async () => {
      try {
        const response = await fetch(fetchUrlEndpoint!);
        if (!response.ok) {
          console.error("Failed to fetch presigned URL");
          return null;
        }
        const data = await response.json();
        return data.pictureUrl || data.logoUrl || null;
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
        return null;
      }
    },
  });

  const invalidatePresignedUrl = () => {
    if (fetchUrlEndpoint) {
      queryClient.removeQueries({ queryKey: ["profile-picture-url", fetchUrlEndpoint] });
    }
  };

  // Set preview URL when currentPicture (or the cached presigned URL) changes
  useEffect(() => {
    if (!currentPicture) {
      setPreviewUrl(null);
      return;
    }

    // If it's a base64 or data URL, use it directly (backward compatibility)
    if (currentPicture.startsWith('data:')) {
      setPreviewUrl(currentPicture);
      return;
    }

    // S3 key — use the cached presigned URL
    if (currentPicture.startsWith('v3/')) {
      setPreviewUrl(presignedUrl ?? null);
      return;
    }

    // For all other cases (HTTP URLs), use directly
    setPreviewUrl(currentPicture);
  }, [currentPicture, presignedUrl]);

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-16 w-16",
    xl: "h-24 w-24"
  };

  const iconSizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-6 w-6",
    xl: "h-8 w-8"
  };

  const initials = firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() : '';

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPG, PNG, GIF)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    // Logos: upload as-is (no crop — they don't fit a circle anyway).
    if (isLogo) {
      handleCropped(file);
      return;
    }

    // Open crop tool; actual upload happens on crop save
    setCropSrc(URL.createObjectURL(file));
  };

  const handleCropped = async (blob: Blob) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setIsSaving(true);

    // If we have an upload endpoint (S3), use it
    if (uploadEndpoint) {
      try {
        const formData = new FormData();
        formData.append(fieldName, blob, 'picture.png');

        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();

        // Drop any cached presigned URL — the object changed, and a browser
        // cache hit on the old URL would show the previous image.
        invalidatePresignedUrl();

        // Update preview with presigned URL from response
        setPreviewUrl(data.pictureUrl || data.logoUrl);

        // Call the callback with null to trigger refetch
        await onPictureChange(null);

        setIsDialogOpen(false);
      } catch (error) {
        console.error('Failed to upload picture:', error);
        toast({
          title: "Upload failed",
          description: "Failed to upload picture. Please try again.",
          variant: "destructive",
        });
        setPreviewUrl(previewUrl);
      } finally {
        setIsSaving(false);
      }
    } else {
      // Fallback to base64 (backward compatibility)
      const reader = new FileReader();
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        setPreviewUrl(result);
        try {
          await onPictureChange(result);
          setIsDialogOpen(false);
        } catch (error) {
          console.error('Failed to save picture:', error);
          toast({
            title: "Save failed",
            description: "Failed to save picture. Please try again.",
            variant: "destructive",
          });
          setPreviewUrl(currentPicture || null);
        } finally {
          setIsSaving(false);
        }
      };
      reader.readAsDataURL(blob);
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePicture = async () => {
    setIsSaving(true);
    try {
      // If we have a delete endpoint, use it
      if (deleteEndpoint) {
        const response = await fetch(deleteEndpoint, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Delete failed');
        }

        invalidatePresignedUrl();
        await onPictureChange(null);
      } else {
        // Fallback to calling onPictureChange with null
        await onPictureChange(null);
      }

      setPreviewUrl(null);
      setIsDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to remove picture:', error);
      toast({
        title: "Remove failed",
        description: "Failed to remove picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSaving) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleRemovePicture();
  };

  return (
    <div
      className="relative inline-block cursor-pointer"
      onMouseEnter={() => showEditOnHover && setIsHovered(true)}
      onMouseLeave={() => showEditOnHover && setIsHovered(false)}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isSaving}
      />

      <div onClick={handleAvatarClick}>
        <Avatar className={`${sizeClasses[size]} ${isLogo ? "rounded-md bg-muted" : ""}`}>
          <AvatarImage
            src={previewUrl || undefined}
            alt={`${firstName} ${lastName}`}
            className={isLogo ? "object-contain" : "object-cover"}
            onLoadingStatusChange={setImageStatus}
          />
          <AvatarFallback className="bg-muted text-muted-foreground">
            {(isS3Picture && presignedLoading) || imageStatus === "loading" ? (
              // A picture exists and is on its way — pulse instead of
              // flashing initials that get replaced a moment later.
              <div
                className={`h-full w-full animate-pulse bg-muted-foreground/20 ${isLogo ? "rounded-md" : "rounded-full"}`}
                aria-label="Loading picture"
              />
            ) : (
              initials || fallbackIcon || <User className={iconSizeClasses[size]} />
            )}
          </AvatarFallback>
        </Avatar>

        {/* Hover overlay */}
        {showEditOnHover && (isHovered || !previewUrl) && (
          <div className={`absolute inset-0 bg-black/50 ${isLogo ? "rounded-md" : "rounded-full"} flex items-center justify-center transition-opacity pointer-events-none`}>
            <div className="text-white text-center">
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Camera className={iconSizeClasses[size]} />
                  <p className="text-xs mt-1">{previewUrl ? 'Change' : 'Add'}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Remove button - shows on hover when there's a picture */}
      {previewUrl && showEditOnHover && isHovered && !isSaving && (
        <button
          onClick={handleRemoveClick}
          className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-colors"
          title="Remove photo"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {cropSrc && (
        <ImageCropDialog
          imageSrc={cropSrc}
          open={true}
          onCancel={handleCropCancel}
          onCropped={handleCropped}
        />
      )}
    </div>
  );
}